/**
 * This file may contain code that uses generative AI for code assistance, unit testing and/or entire functions.
 * The generative model(s) used may be a combination of GitHub Copilot, OpenAI ChatGPT or others.
 *
 * Animated progress card for the intent router. Consumer supplies toolCatalog via config.
 */

import { useMemo } from 'react';

import CommandIntelligenceStepIcon from './CommandIntelligenceStepIcon.jsx';
import { STAGE_STATES } from './chat/generationStepStates';
import AutoAwesome from '@mui/icons-material/AutoAwesome';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import { Box, Button, Chip, Paper, Stack, Typography } from '@mui/material';

const stagePendingDotSx = {
  width: 10,
  height: 10,
  borderRadius: '50%',
  border: 1.5,
  borderColor: 'divider',
};

const StageIcon = ({ state }) => <CommandIntelligenceStepIcon state={state} pendingDotSx={stagePendingDotSx} />;

const formatToolLabels = (recommendedTools, catalog) => {
  if (!Array.isArray(recommendedTools) || recommendedTools.length === 0) {
    return [];
  }

  const lookup = new Map(catalog.map(tool => [tool.id, tool.label]));

  return recommendedTools.map(id => ({
    id,
    label: lookup.get(id) || id,
  }));
};

const stageRowSx = active => ({
  display: 'flex',
  alignItems: 'flex-start',
  gap: 1,
  color: active ? 'text.primary' : 'text.secondary',
});

const stageIconBoxSx = {
  width: 20,
  height: 20,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  mt: '2px',
};

const stageLabelSx = active => ({
  fontSize: '0.82rem',
  fontWeight: active ? 500 : 400,
  lineHeight: 1.35,
});

const IntentProgressCard = ({
  status,
  envelope,
  attachedCount,
  collapsed,
  onToggleCollapsed,
  toolCatalog = [],
  getScopeLabel,
}) => {
  const catalog = useMemo(() => (Array.isArray(toolCatalog) ? toolCatalog : []), [toolCatalog]);

  const stages = useMemo(() => {
    if (status === 'idle') {
      return {
        analyzing: STAGE_STATES.PENDING,
        scope: STAGE_STATES.PENDING,
        tools: STAGE_STATES.PENDING,
        suggestions: STAGE_STATES.PENDING,
      };
    }

    if (status === 'loading') {
      return {
        analyzing: STAGE_STATES.ACTIVE,
        scope: attachedCount > 0 ? STAGE_STATES.DONE : STAGE_STATES.PENDING,
        tools: STAGE_STATES.PENDING,
        suggestions: STAGE_STATES.PENDING,
      };
    }

    if (status === 'ready') {
      return {
        analyzing: STAGE_STATES.DONE,
        scope: STAGE_STATES.DONE,
        tools: STAGE_STATES.DONE,
        suggestions: STAGE_STATES.DONE,
      };
    }

    return {
      analyzing: STAGE_STATES.DONE,
      scope: STAGE_STATES.DONE,
      tools: STAGE_STATES.PENDING,
      suggestions: STAGE_STATES.PENDING,
    };
  }, [status, attachedCount]);

  const tools = useMemo(
    () => formatToolLabels(envelope?.recommendedTools, catalog),
    [envelope?.recommendedTools, catalog]
  );

  const scopeLabel = (() => {
    if (envelope?.scopeLabel) {
      return envelope.scopeLabel;
    }

    if (typeof getScopeLabel === 'function') {
      return getScopeLabel({ attachedCount });
    }

    if (attachedCount === 0) {
      return 'no items attached';
    }

    if (attachedCount === 1) {
      return 'single item';
    }

    return `${attachedCount} items attached`;
  })();

  if (collapsed && status === 'ready') {
    return (
      <Paper
        variant="outlined"
        sx={{
          mb: 2,
          px: 1.5,
          py: 1,
          borderRadius: 2,
          bgcolor: 'action.hover',
          display: 'flex',
          alignItems: 'center',
          gap: 0.75,
          flexWrap: 'wrap',
        }}
      >
        <AutoAwesome sx={{ fontSize: 14, color: 'primary.main' }} />
        {envelope?.intent ? (
          <Chip
            size="small"
            label={`Intent: ${envelope.intent}`}
            color="primary"
            variant="outlined"
            sx={{ fontSize: '0.72rem', height: 22 }}
          />
        ) : null}
        <Chip
          size="small"
          label={scopeLabel}
          variant="outlined"
          sx={{ fontSize: '0.72rem', height: 22, borderColor: 'divider' }}
        />
        {tools.length > 0 ? (
          <Chip
            size="small"
            label={`${tools.length} tool${tools.length === 1 ? '' : 's'}`}
            variant="outlined"
            sx={{ fontSize: '0.72rem', height: 22, borderColor: 'divider' }}
          />
        ) : null}
        <Button
          size="small"
          onClick={onToggleCollapsed}
          endIcon={<ExpandMore sx={{ fontSize: 14 }} />}
          sx={{
            ml: 'auto',
            textTransform: 'none',
            fontSize: '0.72rem',
            color: 'text.secondary',
            minWidth: 'auto',
            '&:hover': { bgcolor: 'transparent', color: 'primary.main' },
          }}
        >
          show details
        </Button>
      </Paper>
    );
  }

  return (
    <Paper
      variant="outlined"
      sx={{
        mb: 2,
        p: 1.75,
        borderRadius: 2,
        bgcolor: 'action.hover',
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.25 }}>
        <AutoAwesome sx={{ fontSize: 16, color: 'primary.main' }} />
        <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: '0.82rem' }}>
          Intent router
        </Typography>
        {status === 'ready' && onToggleCollapsed ? (
          <Button
            size="small"
            onClick={onToggleCollapsed}
            endIcon={<ExpandLess sx={{ fontSize: 14 }} />}
            sx={{
              ml: 'auto',
              textTransform: 'none',
              fontSize: '0.72rem',
              color: 'text.secondary',
              minWidth: 'auto',
              '&:hover': { bgcolor: 'transparent', color: 'primary.main' },
            }}
          >
            collapse
          </Button>
        ) : null}
      </Stack>

      <Stack spacing={0.75}>
        <Box sx={stageRowSx(stages.analyzing !== STAGE_STATES.PENDING)}>
          <Box sx={stageIconBoxSx}>
            <StageIcon state={stages.analyzing} />
          </Box>
          <Typography sx={stageLabelSx(stages.analyzing !== STAGE_STATES.PENDING)}>
            Analyzing your request
            {envelope?.intent ? (
              <Typography
                component="span"
                sx={{ ml: 0.75, fontSize: '0.72rem', color: 'primary.main', fontWeight: 600 }}
              >
                {envelope.intent}
              </Typography>
            ) : null}
          </Typography>
        </Box>

        <Box sx={stageRowSx(stages.scope !== STAGE_STATES.PENDING)}>
          <Box sx={stageIconBoxSx}>
            <StageIcon state={stages.scope} />
          </Box>
          <Typography sx={stageLabelSx(stages.scope !== STAGE_STATES.PENDING)}>
            {stages.scope === STAGE_STATES.PENDING ? 'Scope' : `Scope: ${scopeLabel}`}
          </Typography>
        </Box>

        <Box sx={stageRowSx(stages.tools !== STAGE_STATES.PENDING)}>
          <Box sx={stageIconBoxSx}>
            <StageIcon state={stages.tools} />
          </Box>
          <Box sx={{ flexGrow: 1 }}>
            <Typography sx={stageLabelSx(stages.tools !== STAGE_STATES.PENDING)}>
              {stages.tools === STAGE_STATES.DONE && tools.length > 0 ? 'Recommended tools' : 'Picking relevant tools'}
            </Typography>
            {stages.tools === STAGE_STATES.DONE && tools.length > 0 ? (
              <Stack direction="row" flexWrap="wrap" gap={0.5} sx={{ mt: 0.5 }}>
                {tools.map(tool => (
                  <Chip
                    key={tool.id}
                    size="small"
                    label={tool.label}
                    color="primary"
                    variant="outlined"
                    sx={{ fontSize: '0.72rem', height: 22 }}
                  />
                ))}
              </Stack>
            ) : null}
          </Box>
        </Box>

        <Box sx={stageRowSx(stages.suggestions !== STAGE_STATES.PENDING)}>
          <Box sx={stageIconBoxSx}>
            <StageIcon state={stages.suggestions} />
          </Box>
          <Typography sx={stageLabelSx(stages.suggestions !== STAGE_STATES.PENDING)}>
            {stages.suggestions === STAGE_STATES.DONE ? 'Suggested questions below' : 'Drafting suggestions'}
          </Typography>
        </Box>
      </Stack>
    </Paper>
  );
};

export default IntentProgressCard;
export { STAGE_STATES } from './chat/generationStepStates';
